/**
 * Keeps checkout and bank authorization identifiers out of PostHog.
 *
 * A checkout id rides in a session_id query parameter, or in a next parameter that returns to the
 * checkout, and PostHog records page URLs on every event and every request in a session replay.
 */
import type { CapturedNetworkRequest, CaptureResult } from 'posthog-js'

import { scrubRecordedBodies } from '@/utils/replayNetwork'

const PRIVATE_QUERY_PARAMS = ['session_id', 'next', 'oauth_state_id']
const POSTHOG_URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
]

function redactCheckoutUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin)
    if (!PRIVATE_QUERY_PARAMS.some((param) => url.searchParams.has(param))) return value
    for (const param of PRIVATE_QUERY_PARAMS) url.searchParams.delete(param)
    return url.toString()
  } catch {
    return value
  }
}

/** Strip checkout ids from the URLs an event carries, leaving the rest of each URL for attribution. */
export function redactCheckoutIds(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  // The SDK's broad personal-data mask also removes paid-campaign attribution.
  for (const properties of [
    event.properties,
    event.$set,
    event.$set_once,
    event.properties.$set,
    event.properties.$set_once,
  ]) {
    if (!properties) continue
    for (const key of POSTHOG_URL_PROPERTIES) {
      if (typeof properties[key] === 'string') {
        properties[key] = redactCheckoutUrl(properties[key])
      }
    }
  }
  return event
}

/** Omit billing and Plaid requests from replay and strip authorization parameters from other URLs. */
export function scrubRecordedRequest(request: CapturedNetworkRequest): CapturedNetworkRequest | null {
  const url = new URL(request.name, window.location.origin)
  if (
    url.pathname.startsWith('/api/v1/bookkeeper/billing') ||
    url.pathname.startsWith('/api/v1/integrations/plaid/') ||
    url.hostname === 'plaid.com' ||
    url.hostname.endsWith('.plaid.com')
  )
    return null
  return scrubRecordedBodies({ ...request, name: redactCheckoutUrl(request.name) })
}
